<!-- EXHAUSTIN. — the game over screen, for every mode.

     In huge letters, as the brief asks, and then the numbers underneath. For
     Endurance the headline is the TIME, spelled out in words, because that is
     the thing he is chasing.

     IT MUST RENDER WITH NO RESULT. QA opens every critical route cold and a
     person can land here from a deep link, so "no run to show" is a state this
     screen handles rather than an impossible one. -->
<template>
  <IonPage>
    <!-- No back button, deliberately. Results is reached with router.replace(),
         so the game it is reporting on is gone from the stack: there is nothing
         behind this screen to return to, and IonBackButton was rendering a
         chevron that did nothing when tapped. The way out is the two explicit
         actions below - "Go again" and "Pick another mode", the latter doing
         exactly what a back button would have done - plus the tab bar. -->
    <AppHeader title="Results" />

    <IonContent :fullscreen="true">
      <template v-if="r">
        <div class="result-hero">
          <p class="ex-display xl">EXHAUSTIN.</p>

          <p v-if="r.mode === 'endurance'" class="time">
            You lasted {{ spellDuration(r.lastedMs) }}.
          </p>
          <p v-else class="time">
            {{ (r.score ?? 0).toLocaleString() }} points in {{ modeName(r.mode) }}.
          </p>

          <p v-if="r.isRecord" class="record">{{ recordLine }}</p>
          <p
            v-else-if="r.mode === 'endurance' && r.previousBest"
            class="time"
            style="font-size: var(--text-md); opacity: .8"
          >
            Your record is still {{ spellDuration(r.previousBest) }}.
          </p>
          <p v-else class="time" style="font-size: var(--text-md); opacity: .8">{{ signOff }}</p>
        </div>

        <div class="scoreline">
          <div class="cell"><span class="n">{{ r.correct ?? 0 }}</span><span class="k">Right</span></div>
          <div class="cell"><span class="n">{{ r.wrong ?? 0 }}</span><span class="k">Wrong</span></div>
          <div class="cell"><span class="n">{{ r.bestChain ?? 0 }}</span><span class="k">Streak</span></div>
          <div class="cell"><span class="n">{{ r.ausBonuses ?? 0 }}</span><span class="k">AUS</span></div>
        </div>

        <div class="ex-total">
          <div>
            <div class="k">Added to your total</div>
            <div class="n tabular">+{{ (r.score ?? 0).toLocaleString() }}</div>
          </div>
          <div class="center">
            <div class="k">Running total</div>
            <div class="n tabular">{{ (profile.totalScore ?? 0).toLocaleString() }}</div>
          </div>
        </div>

        <template v-if="r.newTrophies?.length">
          <p class="ex-eyebrow pad">Unlocked just now</p>
          <div class="trophy-grid">
            <div v-for="t in r.newTrophies" :key="t.id" class="trophy">
              <span class="badge" aria-hidden="true">{{ t.icon }}</span>
              <span class="name">{{ t.name }}</span>
              <span class="how">{{ t.description }}</span>
            </div>
          </div>
        </template>

        <div class="pad stack" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="again">{{ againLabel }}</IonButton>
          <IonButton expand="block" fill="outline" @click="$router.replace('/tabs/play')">
            Pick another mode
          </IonButton>
        </div>
      </template>

      <EmptyState
        v-else
        glyph="💨"
        title="No run to show yet."
        body="Finish a game and this is where the damage gets totted up."
        cta="Pick a mode"
        @act="$router.replace('/tabs/play')"
      />

      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader } from '../core/ui';
import EmptyState from '../components/EmptyState.vue';
import { lastResult } from '../game/results.js';
import { spellDuration } from '../game/engine.js';
import { modeName, modeById } from '../game/modes.js';
import { profile } from '../game/profile.js';
import { pun } from '../game/puns.js';

const router = useRouter();
const r = computed(() => lastResult.value);

// Rolled once, not in a computed: a pun that re-rolls on every re-render
// changes its mind while he is reading it.
const signOff = ref(pun('gameOver'));
const recordLine = ref(pun('record'));

const againLabel = computed(() => {
  if (!r.value) return 'Play';
  if (r.value.mode === 'endurance') return 'Go again';
  if (r.value.mode === 'daily') return 'Play something else';
  return `Another go at ${modeName(r.value.mode)}`;
});

function again() {
  const mode = r.value?.mode;
  // The Daily is one attempt a day, so "again" cannot mean the Daily.
  const route = mode === 'daily' ? '/tabs/play' : modeById(mode)?.route ?? '/tabs/play';
  router.replace(route);
}
</script>
